import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Progress } from "../components/ui/progress";
import { UkAreasPicker } from "../components/UkAreasPicker";
import { ServicesPicker } from "../components/ServicesPicker";
import { ProfileCompletenessCard } from "../components/ProfileCompletenessCard";
import {
  Star, ShieldCheck, ShieldWarning, FileText, ChatCircle, Upload, Buildings, Phone,
  EnvelopeSimple, MapPin, IdentificationBadge, ArrowSquareOut, CheckCircle, Warning, XCircle,
  PencilSimple, Plus, Briefcase, Trophy, Timer, Lightning, Coins, ChartBar, Lightbulb,
  CalendarBlank, Folder, ListChecks, ArrowRight, UsersThree,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import OperativeScheduleCalendar from "../components/OperativeScheduleCalendar";
import CalendarSubscriptionCard from "../components/CalendarSubscriptionCard";
import NotificationPreferencesCard from "../components/NotificationPreferencesCard";
import AvailabilityCard from "../components/AvailabilityCard";
import PWAInstallCard from "../components/PWAInstallCard";
import SupplierBadgeRibbon from "../components/SupplierBadgeRibbon";
import WelcomeTour from "../components/WelcomeTour";
import SupplierTeamPanel from "../components/supplier/SupplierTeamPanel";
import { SupplierCrewDialog } from "../components/supplier/SupplierCrewCard";
import SupplierQuotesTab from "../components/SupplierQuotesTab";
import SupplierQuoteInvitations from "../components/SupplierQuoteInvitations";
import PortalKnowledgeBase from "../components/PortalKnowledgeBase";
import { Link } from "react-router-dom";
import { fmtDate } from "../lib/dateUtil";

import DateInput from "../components/DateInput";
const GRADE_STYLES = {
  Excellent: { bg: "bg-emerald-500", text: "text-emerald-600 dark:text-emerald-400", chip: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400 border-emerald-300" },
  Good:      { bg: "bg-sky-500",     text: "text-sky-600 dark:text-sky-400",         chip: "bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-400 border-sky-300" },
  Fair:      { bg: "bg-amber-500",   text: "text-amber-600 dark:text-amber-400",     chip: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400 border-amber-300" },
  Slow:      { bg: "bg-rose-500",    text: "text-rose-600 dark:text-rose-400",       chip: "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-400 border-rose-300" },
  Unrated:   { bg: "bg-slate-400",   text: "text-slate-500 dark:text-slate-400",     chip: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300 border-slate-300" },
};

const DOC_TYPES = [
  "insurance", "public_liability", "employers_liability", "professional_indemnity",
  "gas_safe", "niceic", "napit", "chas", "safe_contractor", "constructionline",
  "dbs_check", "cscs_card", "asbestos_awareness", "first_aid", "fire_safety",
  "method_statement", "risk_assessment", "waste_carrier_license", "contract", "accreditation", "other"
];

const STATUS_CONFIG = {
  compliant: { label: "Compliant", icon: CheckCircle, cls: "text-emerald-600 dark:text-emerald-400", bg: "bg-emerald-100 dark:bg-emerald-900/40" },
  warning: { label: "Warning", icon: Warning, cls: "text-amber-600 dark:text-amber-400", bg: "bg-amber-100 dark:bg-amber-900/40" },
  non_compliant: { label: "Non-Compliant", icon: XCircle, cls: "text-red-600 dark:text-red-400", bg: "bg-red-100 dark:bg-red-900/40" },
};

export default function SupplierPortalPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("dashboard");
  const [profile, setProfile] = useState(null);
  const [compliance, setCompliance] = useState(null);
  const [scorecard, setScorecard] = useState(null);
  const [scoreLoading, setScoreLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [myBadges, setMyBadges] = useState([]);

  // New: work surfaces (jobs / projects / tasks)
  const [workSummary, setWorkSummary] = useState(null);
  const [myJobs, setMyJobs] = useState([]);
  const [myEmergencyJobs, setMyEmergencyJobs] = useState([]);
  const [myProjects, setMyProjects] = useState([]);
  const [myTasks, setMyTasks] = useState([]);
  const [myWorkOrders, setMyWorkOrders] = useState([]);

  // Tour state — controlled mode so Settings tab can replay it.
  const [tourOpen, setTourOpen] = useState(false);

  const tourSteps = [
    {
      icon: Briefcase,
      title: `Welcome${profile?.contact_name ? `, ${profile.contact_name.split(" ")[0]}` : ""}!`,
      body: (
        <>
          <p>This is your home for everything NECOS.FM. We've just shipped a few upgrades — let's take 30 seconds so you don't miss them.</p>
        </>
      ),
    },
    {
      icon: ListChecks,
      title: "My Work — all your jobs in one place",
      body: (
        <>
          <p>The new <strong>My Work</strong> tab pulls together your assigned jobs, emergency call-outs, projects you're on, and individual tasks.</p>
          <p className="mt-2">Every item has a <strong>Directions</strong> button that opens Google Maps with the property pre-loaded — perfect for jumping in the van.</p>
        </>
      ),
    },
    {
      icon: CalendarBlank,
      title: "Schedule — see your week at a glance",
      body: (
        <>
          <p>The <strong>Schedule</strong> tab shows your upcoming work in day, week, month, or list views.</p>
          <p className="mt-2">Tap any job to grab a single <strong>.ics</strong> file and add it to your phone's calendar instantly.</p>
        </>
      ),
    },
    {
      icon: FileText,
      title: "Quotes — send us your prices, in your branding",
      body: (
        <>
          <p>The new <strong>Quotes</strong> tab lets you create supplier quotes for us right inside the portal — line items, VAT, terms — and email or download a PDF in <strong>your own branding</strong> (logo, business name, footer).</p>
          <p className="mt-2">Add your own <strong>markup %</strong> on top of your cost prices — your markup stays private, we only ever see your final quoted prices.</p>
        </>
      ),
    },
    {
      icon: Lightbulb,
      title: "Live calendar subscription + reminders",
      body: (
        <>
          <p>Inside <strong>Settings</strong> you can generate a private <strong>webcal</strong> link — paste it into Apple Calendar / Google Calendar / Outlook and your whole schedule appears there, updating itself automatically.</p>
          <p className="mt-2">We'll also email you the next day's jobs every evening at 6pm. Toggle that off any time.</p>
        </>
      ),
    },
    {
      icon: Trophy,
      title: "Track your performance",
      body: (
        <>
          <p>The <strong>Performance</strong> tab shows your live scorecard — average rating, response times, on-time rate, and quick wins to climb the rankings.</p>
          <p className="mt-2">That's it! You can replay this tour any time from <strong>Settings → Replay walkthrough</strong>.</p>
        </>
      ),
    },
  ];

  // Edit profile state
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});

  // Upload doc state
  const [showUpload, setShowUpload] = useState(false);
  const [docForm, setDocForm] = useState({ doc_type: "insurance", document_number: "", description: "", issue_date: "", expiry_date: "", notes: "" });
  const [docFile, setDocFile] = useState(null);  // The actual File object selected by the user
  const [uploading, setUploading] = useState(false);

  const fetchProfile = useCallback(async () => {
    try {
      const { data } = await API.get("/vendor-portal/my-profile");
      setProfile(data);
    } catch {}
  }, []);

  const fetchCompliance = useCallback(async () => {
    try {
      const { data } = await API.get("/vendor-portal/my-compliance-status");
      setCompliance(data);
    } catch {}
  }, []);

  const fetchScorecard = useCallback(async () => {
    setScoreLoading(true);
    try {
      const { data } = await API.get("/supplier-portal/scorecard");
      setScorecard(data);
    } catch (e) {
      if (e.response?.status !== 404) toast.error("Failed to load performance data");
    } finally {
      setScoreLoading(false);
    }
  }, []);

  const fetchMyBadges = useCallback(async () => {
    try {
      const { data } = await API.get("/supplier-portal/my-badges");
      setMyBadges(data?.badges || []);
    } catch {}
  }, []);

  const fetchWorkSurfaces = useCallback(async () => {
    try {
      const [s, j, e, p, t, wo] = await Promise.all([
        API.get("/supplier-portal/work-summary"),
        API.get("/supplier-portal/jobs"),
        API.get("/supplier-portal/emergency-jobs"),
        API.get("/supplier-portal/projects"),
        API.get("/tasks"),
        API.get("/supplier-portal/work-orders"),
      ]);
      setWorkSummary(s.data);
      setMyJobs(j.data.jobs || []);
      setMyEmergencyJobs(e.data.jobs || []);
      setMyProjects(p.data.projects || []);
      setMyTasks(Array.isArray(t.data) ? t.data : (t.data.tasks || []));
      setMyWorkOrders(wo.data.work_orders || []);
    } catch {}
  }, []);

  useEffect(() => {
    Promise.all([fetchProfile(), fetchCompliance(), fetchWorkSurfaces(), fetchMyBadges()]).finally(() => setLoading(false));
  }, [fetchProfile, fetchCompliance, fetchWorkSurfaces, fetchMyBadges]);

  useEffect(() => {
    if (tab === "performance" && !scorecard && !scoreLoading) {
      fetchScorecard();
    }
  }, [tab, scorecard, scoreLoading, fetchScorecard]);

  const handleUpdateProfile = async () => {
    try {
      await API.put("/vendor-portal/my-profile", editForm);
      toast.success("Profile updated");
      setEditing(false);
      fetchProfile();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const handleUploadDoc = async () => {
    if (!docFile) {
      toast.error("Please choose a file to upload.");
      return;
    }
    if (docFile.size > 20 * 1024 * 1024) {
      toast.error("File too large (max 20 MB).");
      return;
    }
    setUploading(true);
    try {
      // Step 1 — upload the file bytes to storage, get back a {file_id, file_name}
      const fd = new FormData();
      fd.append("file", docFile);
      const vendorIdForLink = profile?.id || "";
      const { data: uploaded } = await API.post(
        `/upload?entity_type=compliance&entity_id=${encodeURIComponent(vendorIdForLink)}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      // Step 2 — create the compliance metadata pointing at that file
      await API.post("/vendor-portal/upload-document", {
        ...docForm,
        file_id: uploaded.file_id || uploaded.id,
        file_name: uploaded.file_name || docFile.name,
      });
      toast.success("Document uploaded for review");
      setShowUpload(false);
      setDocForm({ doc_type: "insurance", document_number: "", description: "", issue_date: "", expiry_date: "", notes: "" });
      setDocFile(null);
      fetchCompliance();
      fetchProfile();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  if (!profile) return (
    <div className="text-center py-20">
      <Buildings size={32} className="mx-auto text-muted-foreground mb-3" />
      <p className="text-sm text-muted-foreground">No supplier profile linked to your account.</p>
      <p className="text-xs text-muted-foreground mt-1">Please contact your administrator.</p>
    </div>
  );

  const compStatus = compliance ? STATUS_CONFIG[compliance.status] || STATUS_CONFIG.non_compliant : null;

  return (
    <div data-testid="supplier-portal-page" className="pb-20 md:pb-0">
      {/* Header - mobile optimized */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 sm:mb-6 gap-2">
        <div>
          <h1 className="text-lg sm:text-2xl font-bold font-[Chivo] tracking-tight" data-testid="portal-company-name">{profile.company_name}</h1>
          <p className="text-xs text-muted-foreground mt-0.5">Supplier Portal - Welcome, {user?.name}</p>
        </div>
        {profile.average_rating > 0 && (
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 self-start">
            <Star size={16} weight="fill" className="text-amber-500" />
            <span className="text-sm font-bold">{profile.average_rating}</span>
            <span className="text-[10px] text-muted-foreground">({profile.rating_count} ratings)</span>
          </div>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        {/* Desktop tabs */}
        <TabsList className="mb-4 hidden sm:flex flex-wrap" data-testid="portal-tabs">
          <TabsTrigger value="dashboard" data-testid="portal-tab-dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="work" data-testid="portal-tab-work">My Work</TabsTrigger>
          <TabsTrigger value="schedule" data-testid="portal-tab-schedule">Schedule</TabsTrigger>
          <TabsTrigger value="team" data-testid="portal-tab-team">Team</TabsTrigger>
          <TabsTrigger value="quotes" data-testid="portal-tab-quotes">Quotes</TabsTrigger>
          <TabsTrigger value="performance" data-testid="portal-tab-performance">Performance</TabsTrigger>
          <TabsTrigger value="profile" data-testid="portal-tab-profile">Profile</TabsTrigger>
          <TabsTrigger value="documents" data-testid="portal-tab-documents">Documents</TabsTrigger>
          <TabsTrigger value="compliance" data-testid="portal-tab-compliance">Compliance</TabsTrigger>
          <TabsTrigger value="settings" data-testid="portal-tab-settings">Settings</TabsTrigger>
          <TabsTrigger value="help" data-testid="portal-tab-help">Help</TabsTrigger>
        </TabsList>

        {/* Mobile bottom tab bar - z-[60] to be above global mobile nav */}
        <div className="fixed bottom-0 left-0 right-0 z-[60] bg-background border-t border-border flex sm:hidden overflow-x-auto" data-testid="portal-mobile-nav">
          {[
            { val: "dashboard", label: "Home", icon: Briefcase },
            { val: "work", label: "Work", icon: ListChecks },
            { val: "schedule", label: "Schedule", icon: CalendarBlank },
            { val: "team", label: "Team", icon: UsersThree },
            { val: "quotes", label: "Quotes", icon: FileText },
            { val: "performance", label: "Score", icon: Trophy },
            { val: "documents", label: "Docs", icon: FileText },
            { val: "compliance", label: "Comply", icon: ShieldCheck },
            { val: "help", label: "Help", icon: Lightbulb },
          ].map(t => (
            <button key={t.val} onClick={() => setTab(t.val)}
              className={`flex-1 min-w-[60px] flex flex-col items-center gap-0.5 py-2.5 text-[10px] font-medium transition-colors active:bg-muted/50 ${tab === t.val ? "text-primary" : "text-muted-foreground"}`}
              data-testid={`mobile-tab-${t.val}`}
            >
              <t.icon size={20} weight={tab === t.val ? "fill" : "regular"} />
              {t.label}
            </button>
          ))}
        </div>

        {/* ========== DASHBOARD ========== */}
        <TabsContent value="dashboard">
          <PWAInstallCard />
          <ProfileCompletenessCard onNavigate={setTab} hideWhenComplete />

          {/* Phase 244 — celebrate the latest Best-Supplier badge */}
          {myBadges.length > 0 && (
            <div className="mb-4 sm:mb-6" data-testid="portal-latest-badge">
              <SupplierBadgeRibbon badge={myBadges[0]} showShare />
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 lg:grid-cols-4 mb-4 sm:mb-6" data-testid="portal-dashboard-stats">
            <DashCard icon={Briefcase} label="Open jobs" value={workSummary?.jobs?.open ?? profile.task_count ?? 0} />
            <DashCard icon={Folder} label="Active projects" value={workSummary?.projects?.active ?? 0} color="indigo" />
            <DashCard icon={ListChecks} label="Open tasks" value={workSummary?.tasks?.open ?? 0} color="amber" />
            <DashCard icon={Lightning} label="Open emergencies" value={workSummary?.emergency_jobs?.open ?? 0} color="red" />
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 lg:grid-cols-4 mb-6">
            <DashCard icon={CheckCircle} label="Jobs completed" value={workSummary?.jobs?.completed ?? profile.completed_count ?? 0} color="emerald" />
            <DashCard icon={FileText} label="Documents" value={profile.document_count || 0} color="blue" />
            <DashCard icon={Star} label="Avg Rating" value={profile.average_rating || "N/A"} color="amber" />
            <DashCard icon={CheckCircle} label="Tasks done" value={workSummary?.tasks?.completed ?? 0} color="emerald" />
          </div>

          {/* Compliance Status Banner */}
          {compStatus && (
            <div className={`flex items-center justify-between p-4 rounded-lg border mb-6 ${compStatus.bg} border-current/10`} data-testid="portal-compliance-banner">
              <div className="flex items-center gap-3">
                <compStatus.icon size={24} className={compStatus.cls} weight="duotone" />
                <div>
                  <p className={`text-sm font-semibold ${compStatus.cls}`}>Compliance Status: {compStatus.label}</p>
                  {compliance.missing_documents.length > 0 && (
                    <p className="text-[11px] text-muted-foreground mt-0.5">Missing: {compliance.missing_documents.map(d => d.replace(/_/g, " ")).join(", ")}</p>
                  )}
                </div>
              </div>
              <Button size="sm" variant="outline" onClick={() => setTab("compliance")} data-testid="portal-view-compliance">View Details</Button>
            </div>
          )}

          {/* Pending Works Orders (P282 — awaiting supplier response) */}
          {(() => {
            const pending = (myWorkOrders || []).filter(w => w.sent_at && !w.supplier_response && w.status !== "completed");
            if (pending.length === 0) return null;
            return (
              <div className="border-2 border-blue-300 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-950/20 rounded-lg p-4 mb-6" data-testid="portal-pending-work-orders">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h2 className="text-sm font-semibold font-[Chivo] flex items-center gap-1.5">
                      <Briefcase size={16} weight="duotone" className="text-blue-600" />
                      Works Orders awaiting your response
                    </h2>
                    <p className="text-xs text-muted-foreground mt-0.5">Accept, request changes or decline so the admin knows where you stand.</p>
                  </div>
                  <Badge className="bg-blue-600 text-white">{pending.length}</Badge>
                </div>
                <div className="space-y-2">
                  {pending.slice(0, 5).map(w => (
                    <Link
                      key={w.id}
                      to={`/supplier-portal/work-orders/${w.id}`}
                      className="flex items-center justify-between gap-3 p-3 rounded-md border border-border bg-card hover:border-blue-400 hover:shadow-sm transition"
                      data-testid={`portal-pending-wo-${w.wo_number}`}
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{w.scope || w.wo_number}</p>
                        <p className="text-xs text-muted-foreground font-mono">{w.wo_number} · £{Number(w.total_budget || 0).toLocaleString("en-GB", { minimumFractionDigits: 2 })}</p>
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-blue-600 font-medium shrink-0">
                        Review <ArrowRight size={14} />
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Recent Ratings */}
          {profile.ratings_summary?.recent?.length > 0 && (
            <div className="border border-border rounded-lg p-4 bg-card" data-testid="portal-recent-ratings">
              <h2 className="text-sm font-semibold font-[Chivo] mb-3">Recent Ratings</h2>
              <div className="space-y-2">
                {profile.ratings_summary.recent.map(r => (
                  <div key={r.id} className="flex items-center justify-between p-2.5 rounded bg-muted/30">
                    <div>
                      {r.comment && <p className="text-xs">{r.comment}</p>}
                      {r.project_reference && <p className="text-[10px] text-muted-foreground">Ref: {r.project_reference}</p>}
                      <p className="text-[10px] text-muted-foreground mt-0.5">{r.rated_by_name} - {new Date(r.created_at).toLocaleDateString("en-GB")}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Star size={14} weight="fill" className="text-amber-400" />
                      <span className="text-sm font-bold">{r.overall}/5</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        {/* ========== PERFORMANCE / SCORECARD ========== */}
        <TabsContent value="performance">
          {/* Phase 244 — Best-Supplier badge wall */}
          {myBadges.length > 0 && (
            <div className="mb-5 border border-border rounded-lg p-4 bg-card" data-testid="portal-badge-wall">
              <div className="flex items-center gap-2 mb-3">
                <Trophy size={18} weight="duotone" className="text-amber-500" />
                <h2 className="text-sm font-semibold font-[Chivo]">Best Supplier Awards</h2>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {myBadges.map(b => (
                  <SupplierBadgeRibbon key={b.id} badge={b} showShare data-testid={`portal-badge-${b.id}`} />
                ))}
              </div>
              <p className="text-[10px] text-muted-foreground mt-3">
                Awards are issued automatically on the 1st of each month based on completed work, SLA grade, and client feedback.
              </p>
            </div>
          )}

          {scoreLoading && !scorecard ? (
            <div className="flex justify-center py-16"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>
          ) : !scorecard ? (
            <div className="text-center py-16 border border-dashed border-border rounded-lg" data-testid="scorecard-empty">
              <Trophy size={28} className="mx-auto text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">Performance data unavailable</p>
            </div>
          ) : (
            <ScorecardView data={scorecard} />
          )}
        </TabsContent>

        {/* ========== PROFILE ========== */}
        <TabsContent value="profile">
          <div className="max-w-2xl"><ProfileCompletenessCard onNavigate={setTab} /></div>
          <div className="border border-border rounded-lg p-5 bg-card max-w-2xl" data-testid="portal-profile">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold font-[Chivo]">Company Profile</h2>
              {!editing && <Button size="sm" variant="outline" onClick={() => { setEditForm({ contact_name: profile.contact_name || "", phone: profile.phone || "", address: profile.address || "", website: profile.website || "", sectors: profile.sectors?.length ? profile.sectors : (profile.trade ? profile.trade.split(",").map(s => s.trim()).filter(Boolean) : []), areas_covered: profile.areas_covered?.length ? profile.areas_covered : (profile.coverage_area ? profile.coverage_area.split(",").map(a => a.trim()).filter(Boolean) : []), accreditations: profile.accreditations || "" }); setEditing(true); }} data-testid="portal-edit-profile"><PencilSimple size={14} className="mr-1" /> Edit</Button>}
            </div>
            {editing ? (
              <div className="space-y-3">
                <div><Label className="text-xs">Contact Name</Label><Input value={editForm.contact_name} onChange={e => setEditForm(p => ({ ...p, contact_name: e.target.value }))} data-testid="portal-edit-contact" /></div>
                <div><Label className="text-xs">Phone</Label><Input value={editForm.phone} onChange={e => setEditForm(p => ({ ...p, phone: e.target.value }))} data-testid="portal-edit-phone" /></div>
                <div><Label className="text-xs">Address</Label><Input value={editForm.address} onChange={e => setEditForm(p => ({ ...p, address: e.target.value }))} data-testid="portal-edit-address" /></div>
                <div><Label className="text-xs">Website</Label><Input value={editForm.website} onChange={e => setEditForm(p => ({ ...p, website: e.target.value }))} data-testid="portal-edit-website" /></div>
                <div><Label className="text-xs">Services Offered</Label>
                  <ServicesPicker value={editForm.sectors || []} onChange={(services) => setEditForm(p => ({ ...p, sectors: services }))} testIdPrefix="portal-services" allowAdd={false} />
                </div>
                <div><Label className="text-xs">Areas Covered</Label>
                  <UkAreasPicker value={editForm.areas_covered || []} onChange={(areas) => setEditForm(p => ({ ...p, areas_covered: areas }))} testIdPrefix="portal-areas" />
                </div>
                <div><Label className="text-xs">Accreditations</Label><Input value={editForm.accreditations} onChange={e => setEditForm(p => ({ ...p, accreditations: e.target.value }))} data-testid="portal-edit-accreditations" /></div>
                <div className="flex gap-2 pt-2">
                  <Button onClick={handleUpdateProfile} data-testid="portal-save-profile">Save Changes</Button>
                  <Button variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
                </div>
              </div>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                <InfoRow icon={Buildings} label="Company" value={profile.company_name} />
                <InfoRow icon={IdentificationBadge} label="Contact" value={profile.contact_name} />
                <InfoRow icon={EnvelopeSimple} label="Email" value={profile.email} />
                <InfoRow icon={Phone} label="Phone" value={profile.phone} />
                <InfoRow icon={MapPin} label="Address" value={profile.address} />
                {profile.website && <InfoRow icon={ArrowSquareOut} label="Website" value={profile.website} />}
                {profile.trade && <InfoRow icon={Briefcase} label="Trade" value={profile.trade} />}
                {profile.accreditations && <InfoRow icon={ShieldCheck} label="Accreditations" value={profile.accreditations} />}
                {profile.sectors?.length > 0 && (
                  <div className="sm:col-span-2">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Services Offered</p>
                    <div className="flex flex-wrap gap-1">{profile.sectors.map(s => <Badge key={s} variant="secondary" className="text-[10px]">{s}</Badge>)}</div>
                  </div>
                )}
                {profile.areas_covered?.length > 0 && (
                  <div className="sm:col-span-2">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Areas Covered</p>
                    <div className="flex flex-wrap gap-1">{profile.areas_covered.map(a => <Badge key={a} variant="outline" className="text-[10px]">{a}</Badge>)}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        </TabsContent>

        {/* ========== DOCUMENTS ========== */}
        <TabsContent value="documents">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold font-[Chivo]">My Documents</h2>
            <Button size="sm" onClick={() => setShowUpload(true)} data-testid="portal-upload-doc-btn">
              <Upload size={14} className="mr-1" /> Upload Document
            </Button>
          </div>
          {compliance?.documents?.length === 0 ? (
            <div className="text-center py-12 border border-dashed border-border rounded-lg">
              <FileText size={28} className="mx-auto text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">No documents yet. Upload your first compliance document.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {(compliance?.documents || []).map(d => (
                <div key={d.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-3 sm:p-3.5 border border-border rounded-lg bg-card gap-2 active:bg-muted/30 transition-colors" data-testid={`portal-doc-${d.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="h-10 w-10 sm:h-9 sm:w-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <FileText size={18} className="text-primary" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-semibold">{d.doc_type?.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</p>
                      <p className="text-[10px] text-muted-foreground">{d.document_number || "No ref"} {d.expiry_date && `- Expires: ${fmtDate(d.expiry_date)}`}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-13 sm:ml-0">
                    <StatusBadge status={d.computed_status} />
                    {d.verified ? (
                      <Badge variant="outline" className="text-[10px] text-emerald-600 border-emerald-300">Verified</Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-300">Pending Review</Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ========== COMPLIANCE ========== */}
        <TabsContent value="compliance">
          {!compliance ? (
            <p className="text-sm text-muted-foreground">Loading...</p>
          ) : (
            <div data-testid="portal-compliance-detail">
              {/* Status Card */}
              <div className={`p-5 rounded-lg border mb-6 ${compStatus.bg}`} data-testid="portal-compliance-status">
                <div className="flex items-center gap-3 mb-3">
                  <compStatus.icon size={28} className={compStatus.cls} weight="duotone" />
                  <div>
                    <p className={`text-lg font-bold font-[Chivo] ${compStatus.cls}`}>{compStatus.label}</p>
                    <p className="text-xs text-muted-foreground">Your overall compliance status</p>
                  </div>
                </div>
                <Progress value={compliance.missing_documents.length === 0 ? 100 : Math.round(((4 - compliance.missing_documents.length) / 4) * 100)} className="h-2" />
                <p className="text-[10px] text-muted-foreground mt-2">{4 - compliance.missing_documents.length} of 4 required documents uploaded</p>
              </div>

              {/* Required Documents Checklist */}
              <div className="border border-border rounded-lg p-5 bg-card mb-6" data-testid="portal-required-docs">
                <h2 className="text-sm font-semibold font-[Chivo] mb-3">Required Documents</h2>
                <div className="space-y-2">
                  {compliance.required_documents.map(dt => {
                    const present = !compliance.missing_documents.includes(dt);
                    return (
                      <div key={dt} className="flex items-center justify-between p-2.5 rounded bg-muted/30">
                        <div className="flex items-center gap-2">
                          {present ? <CheckCircle size={16} className="text-emerald-500" weight="fill" /> : <XCircle size={16} className="text-red-400" weight="fill" />}
                          <span className="text-xs">{dt.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</span>
                        </div>
                        {present ? (
                          <Badge variant="outline" className="text-[10px] text-emerald-600 border-emerald-300">Uploaded</Badge>
                        ) : (
                          <Button size="sm" variant="outline" className="text-[10px] h-7" onClick={() => { setDocForm(p => ({ ...p, doc_type: dt })); setShowUpload(true); }} data-testid={`portal-upload-${dt}`}>
                            <Upload size={12} className="mr-1" /> Upload
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Alerts */}
              {compliance.has_expired && (
                <div className="flex items-center gap-2 p-3 mb-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                  <Warning size={16} className="text-red-500 shrink-0" />
                  <p className="text-xs text-red-700 dark:text-red-400">Some of your documents have expired. Please upload updated versions.</p>
                </div>
              )}
              {compliance.has_expiring && (
                <div className="flex items-center gap-2 p-3 mb-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                  <Warning size={16} className="text-amber-500 shrink-0" />
                  <p className="text-xs text-amber-700 dark:text-amber-400">Some documents are expiring soon. Please renew them before they expire.</p>
                </div>
              )}
            </div>
          )}
        </TabsContent>

        {/* ========== TEAM ========== */}
        <TabsContent value="team">
          <SupplierTeamPanel />
        </TabsContent>

        {/* ========== MY WORK ========== */}
        <TabsContent value="work">
          <div className="space-y-6" data-testid="supplier-work-tab">
            <Section title="Assigned jobs" count={myJobs.length}>
              {myJobs.length === 0 ? (
                <EmptyState text="Nothing's assigned to you yet." />
              ) : myJobs.map(j => <WorkItem key={j.id} item={j} kind="job" userId={user?._id} onCrewSaved={fetchWorkSurfaces} />)}
            </Section>

            {myEmergencyJobs.length > 0 && (
              <Section title="Emergency jobs" count={myEmergencyJobs.length} accent="red">
                {myEmergencyJobs.map(j => <WorkItem key={j.id} item={j} kind="emergency_job" userId={user?._id} onCrewSaved={fetchWorkSurfaces} />)}
              </Section>
            )}

            <Section title="My projects" count={myProjects.length} accent="indigo">
              {myProjects.length === 0 ? (
                <EmptyState text="You're not on any projects yet." />
              ) : myProjects.map(p => <ProjectItem key={p.id} project={p} />)}
            </Section>

            <Section title="My tasks" count={myTasks.length} accent="amber">
              {myTasks.length === 0 ? (
                <EmptyState text="No tasks assigned." />
              ) : myTasks.map(t => <TaskItem key={t.id} task={t} />)}
            </Section>
          </div>
        </TabsContent>

        {/* ========== SCHEDULE ========== */}
        <TabsContent value="schedule">
          <div className="space-y-4" data-testid="supplier-schedule-tab">
            <OperativeScheduleCalendar
              apiBasePath="/supplier-portal"
              entityRoutePrefix="/supplier-portal"
            />
          </div>
        </TabsContent>

        {/* ========== QUOTES ========== */}
        <TabsContent value="quotes">
          <div className="space-y-6">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <h2 className="text-base sm:text-lg font-semibold">Quote invitations from NECOS</h2>
              </div>
              <SupplierQuoteInvitations />
            </div>
            <SupplierQuotesTab />
          </div>
        </TabsContent>

        {/* ========== SETTINGS ========== */}
        <TabsContent value="settings">
          <div className="space-y-4" data-testid="supplier-settings-tab">
            <CalendarSubscriptionCard apiBasePath="/supplier-portal" />
            <NotificationPreferencesCard apiBasePath="/supplier-portal" />
            <AvailabilityCard />
            <PWAInstallCard />

            <div className="border border-border rounded-md p-4 flex items-center justify-between gap-3" data-testid="supplier-replay-tour-card">
              <div>
                <p className="text-sm font-semibold">Replay walkthrough</p>
                <p className="text-[11px] text-muted-foreground">Re-watch the 5-step tour of My Work, Schedule, calendar subscription and more.</p>
              </div>
              <Button size="sm" variant="outline" onClick={() => setTourOpen(true)} data-testid="replay-supplier-tour-btn">
                <Lightbulb size={14} className="mr-1.5" /> Replay tour
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* ========== HELP / KNOWLEDGE BASE (Phase 287) ========== */}
        <TabsContent value="help">
          <SupplierHelpTab />
        </TabsContent>
      </Tabs>

      {/* First-login walkthrough — auto-opens once, then can be replayed via Settings */}
      <WelcomeTour
        storageKey="necos_supplier_tour_seen_v2"
        steps={tourSteps}
        autoOpen={!loading}
        open={tourOpen ? true : undefined}
        onOpenChange={setTourOpen}
      />

      {/* Upload Document Dialog */}
      <Dialog open={showUpload} onOpenChange={(v) => { setShowUpload(v); if (!v) setDocFile(null); }}>
        <DialogContent data-testid="portal-upload-dialog">
          <DialogHeader><DialogTitle className="font-[Chivo]">Upload Document</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Document File *</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.heic,.heif,.doc,.docx,image/*,application/pdf"
                  onChange={(e) => setDocFile(e.target.files?.[0] || null)}
                  className="text-xs file:mr-2 file:rounded-md file:border-0 file:bg-primary file:text-primary-foreground file:text-xs file:px-3 file:py-1.5 file:font-medium hover:file:bg-primary/90"
                  data-testid="portal-doc-file-input"
                />
              </div>
              {docFile && (
                <p className="text-[11px] text-emerald-700 dark:text-emerald-400 mt-1 flex items-center gap-1" data-testid="portal-doc-file-selected">
                  <CheckCircle size={11} weight="fill" /> {docFile.name} · {(docFile.size / 1024).toFixed(0)} KB
                </p>
              )}
              <p className="text-[10px] text-muted-foreground mt-1">PDF, image (JPG/PNG/HEIC) or Word doc. Max 20 MB.</p>
            </div>
            <div>
              <Label className="text-xs">Document Type *</Label>
              <Select value={docForm.doc_type} onValueChange={v => setDocForm(p => ({ ...p, doc_type: v }))}>
                <SelectTrigger data-testid="portal-doc-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DOC_TYPES.map(t => <SelectItem key={t} value={t}>{t.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Reference Number</Label><Input value={docForm.document_number} onChange={e => setDocForm(p => ({ ...p, document_number: e.target.value }))} data-testid="portal-doc-ref" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Issue Date</Label><DateInput value={docForm.issue_date} onChange={e => setDocForm(p => ({ ...p, issue_date: e.target.value }))} data-testid="portal-doc-issue" /></div>
              <div><Label className="text-xs">Expiry Date</Label><DateInput value={docForm.expiry_date} onChange={e => setDocForm(p => ({ ...p, expiry_date: e.target.value }))} data-testid="portal-doc-expiry" /></div>
            </div>
            <div><Label className="text-xs">Notes</Label><Textarea value={docForm.notes} onChange={e => setDocForm(p => ({ ...p, notes: e.target.value }))} className="h-16 text-xs" data-testid="portal-doc-notes" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUpload(false)} disabled={uploading}>Cancel</Button>
            <Button onClick={handleUploadDoc} disabled={uploading || !docFile} data-testid="portal-submit-doc">
              {uploading ? "Uploading…" : "Upload"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Phase 287 — Supplier Help Tab (shared embedded KB)
function SupplierHelpTab() {
  return (
    <PortalKnowledgeBase
      role="vendor"
      testIdPrefix="supplier-help"
      placeholder="Search supplier help articles..."
      intro={{ title: "Supplier help guides", text: "Everything you need to know about working through NECOS.FM — works orders, quoting, on-site check-ins, compliance and your performance score." }}
    />
  );
}

function InfoRow({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2">
      <Icon size={14} className="text-muted-foreground mt-0.5 shrink-0" />
      <div>
        <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</p>
        <p className="text-xs">{value}</p>
      </div>
    </div>
  );
}

function DashCard({ icon: Icon, label, value, color = "primary" }) {
  const colors = { primary: "text-primary", emerald: "text-emerald-600 dark:text-emerald-400", blue: "text-blue-600 dark:text-blue-400", amber: "text-amber-600 dark:text-amber-400" };
  return (
    <div className="border border-border rounded-lg p-3 sm:p-4 bg-card active:scale-[0.98] transition-transform" data-testid={`portal-stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <Icon size={18} className={`${colors[color]} mb-1.5 sm:mb-2`} weight="duotone" />
      <p className="text-lg sm:text-xl font-bold font-[Chivo]">{value}</p>
      <p className="text-[9px] sm:text-[10px] text-muted-foreground uppercase tracking-wider mt-0.5">{label}</p>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    valid: { label: "Valid", cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400" },
    expiring_soon: { label: "Expiring", cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400" },
    expired: { label: "Expired", cls: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400" },
    no_expiry: { label: "No Expiry", cls: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400" },
  };
  const c = map[status] || map.no_expiry;
  return <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${c.cls}`}>{c.label}</span>;
}

function ScorecardView({ data }) {
  const grade = data.grade || "Unrated";
  const style = GRADE_STYLES[grade] || GRADE_STYLES.Unrated;
  const maxDist = Math.max(1, ...Object.values(data.grade_distribution || {}));
  const hasRatings = (data.recent_ratings || []).length > 0;
  const hasTips = (data.recent_tips || []).length > 0;

  return (
    <div className="space-y-4" data-testid="scorecard-view">
      {/* Hero grade card */}
      <div className="relative overflow-hidden border border-border rounded-xl bg-card p-5 sm:p-6" data-testid="scorecard-hero">
        <div className={`absolute top-0 left-0 right-0 h-1 ${style.bg}`} />
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className={`h-16 w-16 sm:h-20 sm:w-20 rounded-2xl ${style.bg} flex items-center justify-center shrink-0`}>
              <Trophy size={32} weight="fill" className="text-white" />
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Your SLA Grade</p>
              <p className={`text-3xl sm:text-4xl font-bold font-[Chivo] ${style.text}`} data-testid="scorecard-grade">{grade}</p>
              {data.trade && <p className="text-xs text-muted-foreground mt-0.5">{data.trade}</p>}
            </div>
          </div>
          {data.peer_rank && data.peer_total > 1 && (
            <div className="text-right" data-testid="scorecard-rank">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Rank in your trade</p>
              <p className="text-2xl font-bold font-[Chivo]">#{data.peer_rank} <span className="text-sm text-muted-foreground font-normal">of {data.peer_total}</span></p>
              {data.percentile != null && <p className="text-[11px] text-muted-foreground mt-0.5">Top {100 - data.percentile + 1}% percentile</p>}
            </div>
          )}
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KPICard icon={Timer} color="sky" label="Avg Response" value={data.avg_response_label || "—"} sub={`${data.jobs_tracked_for_sla || 0} tracked`} testId="kpi-response" />
        <KPICard icon={CheckCircle} color="emerald" label="Completed" value={data.completed_jobs ?? 0} sub={`${data.active_jobs ?? 0} active`} testId="kpi-completed" />
        <KPICard icon={Star} color="amber" label="Avg Rating" value={data.average_rating_stars != null ? `${data.average_rating_stars}★` : "—"} sub={`${data.rating_count || 0} reviews`} testId="kpi-rating" />
        <KPICard icon={Coins} color="violet" label="Recent Tips" value={data.tip_total_last_25 ? `£${data.tip_total_last_25.toFixed(2)}` : "£0"} sub={`${(data.recent_tips || []).length} tips`} testId="kpi-tips" />
      </div>

      {/* Grade distribution */}
      <div className="border border-border rounded-lg p-4 sm:p-5 bg-card" data-testid="scorecard-distribution">
        <div className="flex items-center gap-2 mb-3">
          <ChartBar size={16} className="text-primary" weight="duotone" />
          <h3 className="text-sm font-semibold font-[Chivo]">Grade distribution {data.trade ? <span className="text-muted-foreground font-normal">· {data.trade} peers</span> : null}</h3>
        </div>
        <div className="space-y-2">
          {["Excellent", "Good", "Fair", "Slow", "Unrated"].map(g => {
            const count = data.grade_distribution?.[g] || 0;
            const pct = Math.round((count / maxDist) * 100);
            const gs = GRADE_STYLES[g];
            const isYou = g === grade;
            return (
              <div key={g} className="flex items-center gap-3" data-testid={`dist-${g.toLowerCase()}`}>
                <span className={`text-[11px] font-semibold w-16 ${gs.text}`}>{g}</span>
                <div className="flex-1 h-5 rounded bg-muted/50 overflow-hidden relative">
                  <div className={`h-full ${gs.bg} transition-all`} style={{ width: `${pct}%` }} />
                  {isYou && count > 0 && (
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[9px] font-bold text-white mix-blend-difference">YOU</span>
                  )}
                </div>
                <span className="text-xs font-semibold w-10 text-right tabular-nums">{count}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Improvement tips */}
      {(data.tips || []).length > 0 && (
        <div className="border border-border rounded-lg p-4 sm:p-5 bg-card" data-testid="scorecard-tips">
          <div className="flex items-center gap-2 mb-3">
            <Lightbulb size={16} weight="fill" className="text-amber-500" />
            <h3 className="text-sm font-semibold font-[Chivo]">How to climb the rankings</h3>
          </div>
          <div className="space-y-2.5">
            {data.tips.map((t, i) => (
              <div key={i} className="p-3 rounded-lg bg-gradient-to-br from-amber-50 to-transparent dark:from-amber-900/10 border border-amber-200/50 dark:border-amber-800/30" data-testid={`tip-${i}`}>
                <p className="text-xs font-semibold text-amber-900 dark:text-amber-200">{t.title}</p>
                <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">{t.body}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Two column: ratings + tips */}
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="border border-border rounded-lg p-4 sm:p-5 bg-card" data-testid="scorecard-ratings">
          <div className="flex items-center gap-2 mb-3">
            <Star size={16} weight="fill" className="text-amber-500" />
            <h3 className="text-sm font-semibold font-[Chivo]">Recent client ratings</h3>
          </div>
          {hasRatings ? (
            <div className="space-y-2">
              {data.recent_ratings.slice(0, 10).map((r, i) => (
                <div key={i} className="p-2.5 rounded bg-muted/30" data-testid={`rating-${i}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex">
                      {[1, 2, 3, 4, 5].map(s => (
                        <Star key={s} size={12} weight={s <= (r.stars || 0) ? "fill" : "regular"} className={s <= (r.stars || 0) ? "text-amber-400" : "text-slate-300"} />
                      ))}
                    </div>
                    {r.created_at && <span className="text-[10px] text-muted-foreground">{new Date(r.created_at).toLocaleDateString("en-GB")}</span>}
                  </div>
                  {r.comment && <p className="text-[11px] text-foreground/80">{r.comment}</p>}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground py-6 text-center">No ratings yet. Complete jobs for a chance to earn client feedback.</p>
          )}
        </div>

        <div className="border border-border rounded-lg p-4 sm:p-5 bg-card" data-testid="scorecard-tips-received">
          <div className="flex items-center gap-2 mb-3">
            <Coins size={16} weight="fill" className="text-violet-500" />
            <h3 className="text-sm font-semibold font-[Chivo]">Recent tips received</h3>
          </div>
          {hasTips ? (
            <div className="space-y-2">
              {data.recent_tips.slice(0, 10).map((t, i) => (
                <div key={i} className="flex items-center justify-between p-2.5 rounded bg-muted/30" data-testid={`tip-row-${i}`}>
                  <div className="min-w-0">
                    <p className="text-xs font-medium truncate">{t.title || "Task"}</p>
                    {t.at && <p className="text-[10px] text-muted-foreground">{new Date(t.at).toLocaleDateString("en-GB")}</p>}
                  </div>
                  <span className="text-sm font-bold text-violet-600 dark:text-violet-400 tabular-nums shrink-0 ml-2">£{Number(t.amount || 0).toFixed(2)}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground py-6 text-center">No tips yet. Delight clients through the live tracker to earn tips.</p>
          )}
        </div>
      </div>
    </div>
  );
}

function KPICard({ icon: Icon, color, label, value, sub, testId }) {
  const colors = {
    sky: "text-sky-600 dark:text-sky-400",
    emerald: "text-emerald-600 dark:text-emerald-400",
    amber: "text-amber-600 dark:text-amber-400",
    violet: "text-violet-600 dark:text-violet-400",
  };
  return (
    <div className="border border-border rounded-lg p-3 sm:p-4 bg-card" data-testid={testId}>
      <Icon size={18} className={`${colors[color]} mb-1.5`} weight="duotone" />
      <p className="text-lg sm:text-xl font-bold font-[Chivo] leading-tight">{value}</p>
      <p className="text-[9px] sm:text-[10px] text-muted-foreground uppercase tracking-wider mt-0.5">{label}</p>
      {sub && <p className="text-[10px] text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  );
}


function Section({ title, count, accent = "slate", children }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <h2 className="text-base sm:text-lg font-semibold">{title}</h2>
        <Badge variant={accent === "red" ? "destructive" : "secondary"}>{count}</Badge>
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function EmptyState({ text }) {
  return (
    <div className="p-6 text-center text-sm text-muted-foreground border border-dashed border-border rounded-lg">{text}</div>
  );
}

function WorkItem({ item, kind, userId, onCrewSaved }) {
  const slug = kind === "emergency_job" ? "emergency" : "jobs";
  const [crewOpen, setCrewOpen] = useState(false);
  const crewNames = item.vendor_crew_names || [];
  const done = ["completed", "cancelled"].includes(item.status);
  const mine = userId && (item.vendor_crew_ids || []).includes(userId);
  const addr = [item.street_address, item.town_city].filter(Boolean).join(", ");
  const directionsHref = (item.latitude && item.longitude)
    ? `https://www.google.com/maps/dir/?api=1&destination=${item.latitude},${item.longitude}`
    : addr ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(addr)}` : null;
  const sched = item.scheduled_start && typeof item.scheduled_start === "string" && item.scheduled_start.length >= 10
    ? {
        // scheduled_start is `YYYY-MM-DDTHH:MM:SS+00:00` (GMT) — render literally
        // rather than shifting into the supplier's browser TZ.
        date: new Date(`${item.scheduled_start.slice(0, 10)}T00:00:00`).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", year: "numeric" }),
        time: item.scheduled_start.length >= 16 ? item.scheduled_start.slice(11, 16) : "",
      }
    : null;
  return (
    <div className="block p-3 rounded-lg border border-border bg-card hover:border-primary/60 transition" data-testid={`work-item-${item.id}`}>
      <div className="flex items-start justify-between gap-3">
        <Link to={`/supplier-portal/${slug}/${item.id}`} className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            {kind === "emergency_job" ? <Lightning className="h-4 w-4 text-red-500 shrink-0" /> : <Briefcase className="h-4 w-4 text-blue-500 shrink-0" />}
            <h3 className="font-semibold truncate">{item.title}</h3>
            <Badge variant="secondary" className="text-[9px] uppercase">{(item.status || "open").replace(/_/g, " ")}</Badge>
          </div>
          {addr && <p className="text-xs text-muted-foreground mt-0.5 inline-flex items-center gap-1"><MapPin size={12} /> {addr}</p>}
          {sched && <p className="text-[11px] text-muted-foreground mt-0.5 inline-flex items-center gap-1"><CalendarBlank size={11} /> {sched.date}{sched.time ? ` · ${sched.time}` : ""}</p>}
          {crewNames.length > 0 && <p className="text-[11px] text-primary mt-0.5 inline-flex items-center gap-1" data-testid={`work-crew-${item.id}`}><UsersThree size={11} /> {crewNames.join(", ")}</p>}
        </Link>
        {!done && (
          <button type="button" onClick={(e) => { e.stopPropagation(); setCrewOpen(true); }}
            className="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-md border border-border text-[10px] font-medium hover:bg-muted transition"
            data-testid={`work-assign-crew-${item.id}`}>
            <UsersThree size={12} /> {crewNames.length ? "Crew" : "Assign team"}
          </button>
        )}
        {mine && !done && (
          <Link to={`/my-work/${kind === "emergency_job" ? "emergency" : "jobs"}/${item.id}`} onClick={(e) => e.stopPropagation()}
            className="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-md border border-emerald-300 text-emerald-700 text-[10px] font-medium hover:bg-emerald-50 transition"
            data-testid={`work-field-view-${item.id}`}>
            <ArrowRight size={12} /> Field view
          </Link>
        )}
        <SupplierCrewDialog open={crewOpen} onClose={() => setCrewOpen(false)} entityType={kind} entityId={item.id} reference={item.reference} onSaved={() => onCrewSaved?.()} />
        {directionsHref && (
          <a
            href={directionsHref}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-md border border-border text-[10px] font-medium hover:bg-muted transition"
            data-testid={`work-directions-${item.id}`}
          >
            <MapPin size={12} /> Directions
          </a>
        )}
      </div>
    </div>
  );
}

function ProjectItem({ project }) {
  const pct = project.my_task_count > 0 ? Math.round((project.my_task_done / project.my_task_count) * 100) : 0;
  return (
    <Link to={`/supplier-portal/projects/${project.id}`}
      className="block p-3 rounded-lg border border-border bg-card hover:border-primary/60 transition"
      data-testid={`project-item-${project.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <Folder className="h-4 w-4 text-indigo-500 shrink-0" />
            <h3 className="font-semibold truncate">{project.name}</h3>
          </div>
          {project.reference && <p className="text-[11px] text-muted-foreground mt-0.5">{project.reference}</p>}
          {project.my_task_count > 0 && (
            <p className="text-[11px] text-muted-foreground mt-0.5">{project.my_task_done} of {project.my_task_count} of my tasks done · {pct}%</p>
          )}
        </div>
        <ArrowRight className="h-5 w-5 text-muted-foreground shrink-0" />
      </div>
    </Link>
  );
}

function TaskItem({ task }) {
  const overdue = task.due_date && task.status !== "approved" && new Date(task.due_date) < new Date();
  return (
    <Link to={`/supplier-portal/tasks/${task.id}`}
      className="block p-3 rounded-lg border border-border bg-card hover:border-primary/60 transition"
      data-testid={`task-item-${task.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <ListChecks className="h-4 w-4 text-amber-500 shrink-0" />
            <h3 className="font-semibold truncate">{task.title}</h3>
            <Badge variant="secondary" className="text-[9px] uppercase">{(task.status || "new").replace(/_/g, " ")}</Badge>
            {overdue && <Badge variant="destructive" className="text-[9px] uppercase">Overdue</Badge>}
          </div>
          {task.project_name && <p className="text-[11px] text-muted-foreground mt-0.5">in {task.project_name}</p>}
          {task.start_date && <p className="text-[11px] text-emerald-700 dark:text-emerald-400 mt-0.5 font-medium"><CalendarBlank size={11} className="inline -mt-0.5 mr-0.5" />Start {task.start_date}{task.start_time ? ` · ${task.start_time}` : ""}{task.end_date && task.end_date !== task.start_date ? ` → ${task.end_date}` : ""}</p>}
          {task.due_date && <p className="text-[11px] text-muted-foreground mt-0.5"><CalendarBlank size={11} className="inline -mt-0.5 mr-0.5" />Due {fmtDate(task.due_date)}</p>}
          {Array.isArray(task.scheduled_dates) && task.scheduled_dates.filter(s => s.start_date).length > 0 && (
            <p className="text-[11px] text-blue-700 dark:text-blue-400 mt-0.5" data-testid={`task-extra-sessions-count-${task.id}`}>
              <CalendarBlank size={11} className="inline -mt-0.5 mr-0.5" />
              +{task.scheduled_dates.filter(s => s.start_date).length} more session{task.scheduled_dates.filter(s => s.start_date).length !== 1 ? "s" : ""}
            </p>
          )}
        </div>
        <ArrowRight className="h-5 w-5 text-muted-foreground shrink-0" />
      </div>
    </Link>
  );
}
